import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as PointerEventReact,
  type RefObject,
} from 'react';

/**
 * Zoom digital di atas sebuah gambar/video (§G8.2, §G10.6).
 *
 * Lahir di layar penuh dashboard 25 Agu 2026 dan tinggal di sana sendirian
 * sampai 31 Agu 2026, ketika halaman Playback meminta hal yang sama persis.
 * Dipindahkan ke sini bukan demi rapi: dua salinan gestur zoom berarti dua
 * tempat yang harus sepakat soal apa arti satu klik roda, dan begitu keduanya
 * berbeda, orang yang sama merasakan dua aplikasi yang berbeda di dua halaman.
 *
 * "Digital" harfiah: yang diperbesar adalah gambar yang **sudah** sampai. Tidak
 * ada crop di server dan tidak ada PTZ; di atas ±2× yang bertambah cuma ukuran
 * pikselnya. Karena itu batasnya 5× dan bukan angka yang lebih besar.
 */
export const ZOOM_MIN = 1;
export const ZOOM_MAX = 5;

/** Kelipatan sekali ketuk ganda — cukup untuk membaca wajah/plat, belum blur. */
export const ZOOM_KETUK = 2.5;

/** Ketukan kedua dianggap "ketuk ganda" kalau secepat dan sedekat ini. */
const KETUK_MS = 300;
const KETUK_PX = 30;

/** Skala + geser, dalam piksel kotak gambar. `transform-origin` di pojok 0,0. */
export interface Zoom {
  s: number;
  x: number;
  y: number;
}

export const ZOOM_AWAL: Zoom = { s: 1, x: 0, y: 0 };

/**
 * Jaga supaya kotaknya tidak pernah kehabisan gambar di salah satu sisi.
 *
 * Pada skala `s`, gambar yang diperbesar selebar `s×w`, jadi pergeseran yang
 * sah ada di `[-(s-1)w, 0]`. Tanpa penjaga ini satu lemparan jari mengirim
 * gambarnya keluar layar dan yang tersisa cuma bidang hitam — dan tidak ada
 * satu pun petunjuk di layar tentang cara membawanya kembali.
 */
export function rapikan(z: Zoom, w: number, h: number): Zoom {
  const s = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z.s));
  return {
    s,
    x: Math.min(0, Math.max(-(s - 1) * w, z.x)),
    y: Math.min(0, Math.max(-(s - 1) * h, z.y)),
  };
}

/**
 * Perbesar `faktor` kali **ke arah titik `fx,fy`** — titik itu yang tidak
 * bergeser sedikit pun.
 *
 * Zoom yang selalu menuju pusat kotak memaksa orang membesarkan dulu lalu
 * menyeret mencari kembali apa yang tadi dilihatnya. Yang dipakai orang adalah
 * "besarkan **yang ini**": kursor di bawah telunjuk, atau titik tengah dua jari.
 *
 * Faktornya dihitung ulang sesudah dijepit batas (`s / z.s`), bukan dipakai apa
 * adanya: kalau tidak, mendorong roda terus di 5× akan menggeser gambar padahal
 * skalanya sudah berhenti bertambah.
 */
export function zoomKe(z: Zoom, faktor: number, fx: number, fy: number, w: number, h: number): Zoom {
  const s = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z.s * faktor));
  const nyata = s / z.s;
  return rapikan({ s, x: fx - (fx - z.x) * nyata, y: fy - (fy - z.y) * nyata }, w, h);
}

/**
 * Berapa kali lipat satu kejadian roda memperbesar — dan **dari sumber apa**.
 *
 * Satu `wheel` bukan satu hal. Ada tiga sumber yang bentuk angkanya berbeda
 * jauh, dan menyamakan ketiganya adalah bug yang dilaporkan 31 Agu 2026:
 * *"zoom pakai dua jari di touchpad lama banget, nggak kayak scroll mouse.
 * Kalau dicobain orang, frustrasi banget."*
 *
 * | Sumber | `deltaY` sekali kejadian | Frekuensi |
 * |---|---|---|
 * | Roda tetikus | ±100 (atau ±3 `deltaMode=1` di Firefox) | sekali per klik |
 * | Cubit touchpad | ±1 … ±12, **`ctrlKey` menyala** | ±60/detik selama gestur |
 * | Gulir dua jari | ±4 … ±16 | ±60/detik |
 *
 * Dengan satu kepekaan untuk semuanya, angka yang pas untuk roda (satu klik
 * terasa) membuat cubitan touchpad butuh **ratusan** kejadian untuk sampai 5×.
 * Karena itu cubitan punya kepekaannya sendiri, ±5× lebih besar per satuan:
 * satu gestur cubit sepanjang satu detik menempuh hampir seluruh rentang zoom.
 *
 * `deltaMode` ikut disamakan lebih dulu. Firefox mengirim roda tetikus sebagai
 * **baris** (`deltaY = ±3`), dan membacanya sebagai piksel membuat zoom di
 * Firefox nyaris tidak bergerak sama sekali.
 */
export function faktorRodaZoom(e: WheelEvent): number {
  const piksel =
    e.deltaMode === 1 ? e.deltaY * 16 : e.deltaMode === 2 ? e.deltaY * 400 : e.deltaY;

  // Cubit touchpad (dan Ctrl+roda) datang dengan `ctrlKey` menyala — itu satu-
  // satunya penanda yang dikirim browser, dan berlaku di semua peramban modern.
  const kepekaan = e.ctrlKey ? 0.01 : 0.002;

  // Dijepit supaya satu kejadian raksasa — gulir inersia macOS bisa mengirim
  // deltaY ratusan sekaligus — tidak melompat langsung ke batas.
  const dijepit = Math.min(160, Math.max(-160, piksel));
  return Math.exp(-dijepit * kepekaan);
}

export interface OpsiZoom {
  /**
   * Kejadian yang harus dilewatkan begitu saja.
   *
   * Dipakai halaman yang menaruh kendali **di atas** gambarnya — bilah pemutar,
   * tombol layar penuh, daftar klip. Tanpa ini, menekan ⏵ dua kali cepat
   * terbaca sebagai ketuk ganda dan gambarnya ikut membesar.
   */
  lewati?: (target: EventTarget | null) => boolean;
}

/**
 * Gestur zoom lengkap untuk satu kotak: roda tetikus, cubit touchpad, cubit dua
 * jari, seret untuk menggeser, dan ketuk ganda untuk pulang.
 *
 * Yang dikembalikan sengaja bukan komponen melainkan sekumpulan alat: kotak
 * yang di-zoom di dashboard berisi dua `<img>` yang bertukar, di Playback dua
 * `<video>` yang bertukar, dan keduanya punya aturan tumpukannya sendiri.
 */
export function useZoomGambar(kotakRef: RefObject<HTMLElement | null>, opsi: OpsiZoom = {}) {
  const [zoom, setZoom] = useState<Zoom>(ZOOM_AWAL);
  const lewati = opsi.lewati;

  /** Penunjuk yang sedang menyentuh gambar — dua di antaranya berarti mencubit. */
  const jejakRef = useRef(new Map<number, { x: number; y: number }>());
  /** Keadaan cubitan sebelumnya: jaraknya, dan titik tengahnya. */
  const cubitRef = useRef<{ jarak: number; fx: number; fy: number } | null>(null);
  /** Ketukan terakhir, untuk mengenali ketuk ganda tanpa `dblclick`. */
  const ketukRef = useRef<{ t: number; x: number; y: number } | null>(null);

  const kotak = useCallback(
    () => kotakRef.current?.getBoundingClientRect() ?? null,
    [kotakRef],
  );

  const reset = useCallback(() => setZoom(ZOOM_AWAL), []);

  /**
   * Zoom dengan titik acuan pusat kotak — jalan untuk papan ketik dan tombol
   * (§G7 no.3). Tanpa penunjuk, tidak ada titik lain yang bisa disebut.
   */
  const zoomTengah = useCallback(
    (faktor: number) => {
      const r = kotak();
      if (!r) return;
      setZoom((z) => zoomKe(z, faktor, r.width / 2, r.height / 2, r.width, r.height));
    },
    [kotak],
  );

  /**
   * Kotaknya berubah ukuran — dan `x,y` di atas adalah piksel kotak yang
   * **lama** (31 Agustus 2026).
   *
   * Dilaporkan lapangan sebagai gambar yang hilang sama sekali: *"di halaman
   * playback, kalo lg fullscreen dan zoom in trus keluar, videonya jd hitam."*
   * Sebabnya aritmetika, bukan layar penuhnya: pada 3× di kotak 1920×1080
   * geserannya bisa sampai −3.800px, dan begitu kotaknya menyusut jadi 900×506
   * batas yang sah tinggal −1.800px. Geseran lama tidak pernah dihitung ulang,
   * jadi seluruh gambar duduk di luar kotaknya dan yang tersisa bidang hitam —
   * tanpa satu pun petunjuk di layar tentang cara membawanya kembali.
   *
   * Yang dikerjakan di sini bukan sekadar menjepit ulang melainkan
   * **menskalakan** geserannya lebih dulu (`w baru / w lama`): bagian gambar
   * yang sedang dilihat orang tetap bagian yang sama sesudah kotaknya berubah.
   * Menjepit saja akan melemparkan pandangannya ke tepi terdekat, dan keluar
   * dari layar penuh bukan permintaan untuk pindah tempat.
   *
   * Berlaku untuk semua yang mengubah ukuran kotak — masuk/keluar layar penuh,
   * jendela diubah, ponsel diputar, daftar klip dibuka — bukan hanya kejadian
   * yang kebetulan pertama kali ditemukan.
   */
  const ukuranRef = useRef<{ w: number; h: number } | null>(null);
  useEffect(() => {
    const el = kotakRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entri]) => {
      const r = entri!.contentRect;
      const w = Math.round(r.width);
      const h = Math.round(r.height);
      const lama = ukuranRef.current;
      ukuranRef.current = { w, h };
      // Kotak yang belum pernah diukur tidak punya "sebelumnya"; kotak
      // bernilai nol (elemen yang sedang disembunyikan) membuat pembagian di
      // bawah tak berarti.
      if (!lama || lama.w === 0 || lama.h === 0 || w === 0 || h === 0) return;
      if (lama.w === w && lama.h === h) return;
      setZoom((z) =>
        z.s <= 1
          ? z
          : rapikan({ s: z.s, x: (z.x * w) / lama.w, y: (z.y * h) / lama.h }, w, h),
      );
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [kotakRef]);

  /**
   * Roda & cubit touchpad memperbesar **ke arah kursor**.
   *
   * Dipasang sebagai listener asli dengan `passive: false`, bukan lewat
   * `onWheel` React: React memasang `wheel` di akar sebagai listener pasif, dan
   * `preventDefault()` di dalamnya diabaikan browser — halaman di belakang ikut
   * menggulung, dan pada cubitan touchpad seluruh halaman ikut di-zoom browser.
   */
  useEffect(() => {
    const el = kotakRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (lewati?.(e.target)) return;
      e.preventDefault();
      const r = el.getBoundingClientRect();
      setZoom((z) =>
        zoomKe(z, faktorRodaZoom(e), e.clientX - r.left, e.clientY - r.top, r.width, r.height),
      );
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [kotakRef, lewati]);

  /**
   * Sentuhan: satu jari menggeser (hanya kalau sudah diperbesar), dua jari
   * mencubit **dan** menggeser sekaligus.
   *
   * Dua jari yang dibuka sambil bergeser adalah satu gerakan, bukan dua: titik
   * tengahnya ikut dipakai sebagai geseran, jadi mencubit di pojok layar tidak
   * menyeret gambarnya ke tengah dulu.
   *
   * Satu jari **tidak** menggeser selama masih 1×: di sana tidak ada yang bisa
   * digeser, dan gerakan yang tidak menghasilkan apa-apa terbaca sebagai
   * aplikasi yang menggantung.
   */
  const onPointerDown = useCallback(
    (e: PointerEventReact) => {
      const el = kotakRef.current;
      if (!el || lewati?.(e.target)) return;
      el.setPointerCapture(e.pointerId);
      jejakRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (jejakRef.current.size === 2) {
        const [a, b] = [...jejakRef.current.values()];
        const r = el.getBoundingClientRect();
        cubitRef.current = {
          jarak: Math.hypot(a!.x - b!.x, a!.y - b!.y),
          fx: (a!.x + b!.x) / 2 - r.left,
          fy: (a!.y + b!.y) / 2 - r.top,
        };
        ketukRef.current = null;
        return;
      }
      if (jejakRef.current.size > 2) return;

      /**
       * Ketuk ganda / klik ganda: 1× ⇄ 2,5× **di titik yang diketuk**.
       *
       * Dikenali sendiri, tidak menumpang `dblclick`: dengan `touch-action:
       * none` sebagian browser sentuh tidak lagi mengirimkannya, dan jalan
       * pulang ke 1× yang cuma ada di tetikus bukan jalan pulang untuk layar
       * sentuh.
       */
      const now = Date.now();
      const lalu = ketukRef.current;
      if (
        lalu &&
        now - lalu.t < KETUK_MS &&
        Math.hypot(e.clientX - lalu.x, e.clientY - lalu.y) < KETUK_PX
      ) {
        const r = el.getBoundingClientRect();
        ketukRef.current = null;
        setZoom((z) =>
          z.s > 1
            ? ZOOM_AWAL
            : zoomKe(z, ZOOM_KETUK, e.clientX - r.left, e.clientY - r.top, r.width, r.height),
        );
        return;
      }
      ketukRef.current = { t: now, x: e.clientX, y: e.clientY };
    },
    [kotakRef, lewati],
  );

  const onPointerMove = useCallback(
    (e: PointerEventReact) => {
      const jejak = jejakRef.current;
      const titik = jejak.get(e.pointerId);
      const r = kotak();
      if (!titik || !r) return;
      const dx = e.clientX - titik.x;
      const dy = e.clientY - titik.y;
      titik.x = e.clientX;
      titik.y = e.clientY;

      if (jejak.size >= 2) {
        const [a, b] = [...jejak.values()];
        const jarak = Math.hypot(a!.x - b!.x, a!.y - b!.y);
        const fx = (a!.x + b!.x) / 2 - r.left;
        const fy = (a!.y + b!.y) / 2 - r.top;
        const cubit = cubitRef.current;
        cubitRef.current = { jarak, fx, fy };
        if (!cubit || cubit.jarak === 0) return;
        setZoom((z) => {
          const digeser = { ...z, x: z.x + (fx - cubit.fx), y: z.y + (fy - cubit.fy) };
          return zoomKe(digeser, jarak / cubit.jarak, fx, fy, r.width, r.height);
        });
        return;
      }

      // Geseran membatalkan calon ketuk ganda — jari yang menyeret bukan ketukan.
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) ketukRef.current = null;
      setZoom((z) =>
        z.s <= 1 ? z : rapikan({ ...z, x: z.x + dx, y: z.y + dy }, r.width, r.height),
      );
    },
    [kotak],
  );

  const lepasPointer = useCallback((e: PointerEventReact) => {
    jejakRef.current.delete(e.pointerId);
    if (jejakRef.current.size < 2) cubitRef.current = null;
  }, []);

  /** `origin-top-left` wajib menyertainya: rumus di atas menganggap acuannya 0,0. */
  const gaya: CSSProperties = {
    transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.s})`,
    transformOrigin: '0 0',
  };

  return {
    zoom,
    gaya,
    reset,
    zoomTengah,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: lepasPointer,
      onPointerCancel: lepasPointer,
    },
  };
}
